import * as React from 'react';
import { getGlobalEventsLogs } from '@cian/events-log';

import { selectRecommendations, selectRecommendationsStatus } from '../../selectors/recommendations';
import { selectShowHeader } from '../../selectors/showHeader';
import { fetchRecommendations } from '../../slices';
import { useAppDispatch, useAppSelector as useSelector } from '../../types';
import { useApplicationContext } from '../../utils/applicationContext';
import { selectRegionId } from '../../selectors/selectRegionId';

const TOPIC_RECOMMENDATIONS = 'recommendations';

export const useRecommendations = () => {
  const {
    custom: { isMobile },
  } = useApplicationContext();

  const dispatch = useAppDispatch();
  const recommendations = useSelector(selectRecommendations);
  const recommendationsStatus = useSelector(selectRecommendationsStatus);
  const showHeader = useSelector(selectShowHeader);
  const regionId = useSelector(selectRegionId);

  const onRecommendationClick = React.useCallback((analyticsData: object) => {
    const globalLog = getGlobalEventsLogs();

    globalLog.produce(TOPIC_RECOMMENDATIONS, {
      type: 'click',
      value: analyticsData,
    });
  }, []);

  const onShowTrap = React.useCallback((analyticsData: object) => {
    const globalLog = getGlobalEventsLogs();

    globalLog.produce(TOPIC_RECOMMENDATIONS, {
      type: 'showTrap',
      value: analyticsData,
    });
  }, []);

  const onShow = React.useCallback(() => {
    const products = recommendations
      .map(({ offerId: id }, index) => ({
        id,
        position: index + 1,
        offerType: 'offer',
      }))
      .filter(({ id }) => id);

    const globalLog = getGlobalEventsLogs();

    globalLog.produce(TOPIC_RECOMMENDATIONS, {
      type: 'show',
      value: products,
    });
  }, [recommendations]);

  React.useEffect(() => {
    dispatch(fetchRecommendations());
  }, [dispatch]);

  return React.useMemo(
    () => ({
      recommendations,
      showHeader,
      onShowTrap,
      onShow,
      onRecommendationClick,
      recommendationsStatus,
      isMobile,
      regionId,
    }),
    [recommendations, showHeader, onShowTrap, onShow, onRecommendationClick, recommendationsStatus, isMobile, regionId],
  );
};
